import { ReportingSport } from '@frontend/core/lib/domains/reporting/types'
import {
  SoccerPosition,
  soccerPositionResolver
} from '@frontend/core/lib/domains/sports/positions'
import { defaultSinglePitch as soccerPitch } from '@frontend/core/lib/domains/sports/soccer/pitch'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import { ReportingSportConfig, type ReportingSportData } from './base'
import type { DashboardChartMetricDef } from './types'

// All unit-bearing metrics (distance, speed) leave conversion + abbreviation
// to the active unit system — see `buildMetrics` in dashboard-chart-utils.
// Pure counts / percentages stay unitless.
const soccerChartMetrics: DashboardChartMetricDef[] = [
  {
    name: 'Number of Runs',
    key: 'highSpeedRunCount',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Run Distance',
    key: 'highSpeedRunDistance_m',
    fallbackMax: 5000,
    unitType: 'distance'
  },
  {
    name: 'Number of Passes',
    key: 'passesAttempted',
    fallbackMax: 500,
    unitType: 'count'
  },
  {
    name: 'Number of Forward Passes',
    key: 'forwardPasses',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Number of Shots',
    key: 'shotsAttempted',
    fallbackMax: 30,
    unitType: 'count'
  },
  {
    name: 'Shot % Conversion',
    key: 'conversionPct',
    fallbackMax: 100,
    isPercent: true
  },
  {
    name: 'Shot % on Target',
    key: 'shotsOnTargetPct',
    fallbackMax: 100,
    isPercent: true
  },
  {
    name: 'Acceleration Count',
    key: 'highIntensityAccelCount',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Deceleration count',
    key: 'highIntensityDecelCount',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Avg. Max Speed',
    key: 'avgMaxSpeed_mps',
    fallbackMax: 10,
    unitType: 'speed'
  },
  {
    name: 'Total Distance',
    key: 'totalDistance_m',
    fallbackMax: 80000,
    unitType: 'distance'
  }
]

const soccerData: ReportingSportData<SoccerPosition> = {
  sport: ReportingSport.Soccer,
  displayName: 'Soccer',
  emoji: '⚽',
  themeClass: 'sport-soccer',
  periodLabel: 'Half',
  periodPluralLabel: 'Halves',
  pitch: {
    sport: soccerPitch.sport,
    coordinates: soccerPitch.pitch.coordinates ?? {}
  },

  positionFilters: [
    { value: '', label: 'All positions', canonical: [] },
    {
      value: 'GKP',
      label: 'Goalkeeper',
      canonical: [SoccerPosition.Goalkeeper]
    },
    { value: 'DEF', label: 'Defender', canonical: [SoccerPosition.Defender] },
    {
      value: 'MID',
      label: 'Midfielder',
      canonical: [SoccerPosition.Midfielder]
    },
    { value: 'FWD', label: 'Forward', canonical: [SoccerPosition.Forward] }
  ],

  resolvePosition: soccerPositionResolver.resolve,

  positionColors: {
    [SoccerPosition.Goalkeeper]: '#f59e0b',
    [SoccerPosition.Defender]: '#3b82f6',
    [SoccerPosition.Midfielder]: '#10b981',
    [SoccerPosition.Forward]: '#ef4444'
  },

  playCategories: [
    {
      category: 'Pass',
      label: 'Pass',
      actionTypes: ['SoccerPass'],
      filterField: 'passingPlayerId',
      color: '#ef4444'
    },
    {
      category: 'Cross',
      label: 'Cross',
      actionTypes: ['SoccerCross'],
      filterField: 'passingPlayerId',
      color: '#38bdf8'
    },
    {
      category: 'Shot',
      label: 'Shot',
      actionTypes: ['SoccerShotAtGoal'],
      color: '#f97316'
    },
    {
      category: 'Carry',
      label: 'Carry',
      actionTypes: ['SoccerDribble'],
      color: '#22c55e'
    }
  ],

  dashboard: {
    summaryMetricKeys: [
      'totalDistance_m',
      'avgMaxSpeed_mps',
      'highIntensityAccelCount',
      'involvements',
      'carries'
    ],
    topListKeys: ['passesAttempted', 'shotsAttempted'],
    chartMetrics: soccerChartMetrics
  },

  playerPerformance: {
    staticColumns: [
      { accessor: 'period', label: 'Period' },
      { accessor: 'duration', label: 'Duration' }
    ],
    summaryColumns: [
      { accessor: 'passesAttempted', label: 'Passes', unitType: 'count' },
      { accessor: 'passCompletionPct', label: 'Pass %' },
      { accessor: 'forwardPassCompletionPct', label: 'Fwd %' },
      { accessor: 'shotsAttempted', label: 'Shots', unitType: 'count' },
      { accessor: 'conversionPct', label: 'Conv %' },
      {
        accessor: 'totalDistance_m',
        label: 'Distance',
        unitType: 'distance'
      },
      {
        accessor: 'distancePerMinute_m',
        label: 'Dist/min',
        unitType: 'distance'
      },
      { accessor: 'sprintCount', label: 'Sprints', unitType: 'count' }
    ],
    tabGroups: [
      {
        key: 'technical',
        label: 'Technical',
        tabs: [
          {
            key: 'all_technical',
            label: 'All',
            columns: [
              {
                accessor: 'involvements',
                label: 'Involvement Count',
                unitType: 'count'
              },
              { accessor: 'involvementsP90', label: 'Involvements P90' },
              {
                accessor: 'involvementsPerMinute',
                label: 'Involvements / Min'
              },
              { accessor: 'passCompletionPct', label: 'Pass Success %' },
              { accessor: 'conversionPct', label: 'Shot Success %' }
            ]
          },
          {
            key: 'pass',
            label: 'Pass',
            columns: [
              {
                accessor: 'passesAttempted',
                label: 'Count',
                unitType: 'count'
              },
              { accessor: 'passesAttemptedP90', label: 'Count p90' },
              { accessor: 'passCompletionPct', label: 'Completed %' },
              {
                accessor: 'forwardPassCompletionPct',
                label: 'Forward Pass Completed %'
              },
              {
                accessor: 'passTimeOnBallAvg_s',
                label: 'Avg Time On Ball',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'shot',
            label: 'Shot',
            columns: [
              { accessor: 'shotsAttempted', label: 'Count', unitType: 'count' },
              { accessor: 'shotsAttemptedP90', label: 'Count p90' },
              { accessor: 'conversionPct', label: 'Success %' },
              {
                accessor: 'shotSpeedAvg_mps',
                label: 'Shot Velocity Avg',
                unitType: 'speed'
              },
              {
                accessor: 'shotDistanceFromGoalAvg_m',
                label: 'Distance From Goal Avg',
                unitType: 'distance'
              }
            ]
          },
          {
            key: 'carry',
            label: 'Carry',
            columns: [
              { accessor: 'carries', label: 'Count', unitType: 'count' },
              { accessor: 'carriesP90', label: 'Count p90' },
              {
                accessor: 'carrySpeedAvg_mps',
                label: 'Carry Velocity Avg',
                unitType: 'speed'
              }
            ]
          }
        ]
      },
      {
        key: 'physical',
        label: 'Physical',
        tabs: [
          {
            key: 'physical',
            label: 'Physical',
            columns: [
              {
                accessor: 'totalDistance_m',
                label: 'Distance',
                unitType: 'distance'
              },
              {
                accessor: 'distancePerMinute_m',
                label: 'Distance / Min.',
                unitType: 'distance'
              },
              {
                accessor: 'highSpeedRunDistance_m',
                label: 'High Speed Running Distance',
                unitType: 'distance'
              },
              {
                accessor: 'highIntensityAccelCount',
                label: 'Accel Count',
                unitType: 'count'
              },
              {
                accessor: 'highIntensityDecelCount',
                label: 'Decel Count',
                unitType: 'count'
              },
              {
                accessor: 'totalMechanicalWork_kJ',
                label: 'Total Work',
                unitType: 'energy'
              },
              {
                accessor: 'maxSpeed_mps',
                label: 'Max Speed',
                unitType: 'speed'
              }
            ]
          },
          {
            key: 'technical_load',
            label: 'Technical Load',
            columns: [
              {
                accessor: 'involvements',
                label: 'Involvements',
                unitType: 'count'
              },
              {
                accessor: 'involvementsPerMinute',
                label: 'Involvements / Min'
              },
              { accessor: 'involvementsP90', label: 'Involvements P90' },
              {
                accessor: 'kicks',
                label: 'Kick Count Total',
                unitType: 'count'
              },
              { accessor: 'kicksPerMinute', label: 'Kick Count / Min' }
            ]
          }
        ]
      }
    ]
  },

  playerCompare: {
    staticColumns: [
      { accessor: 'playerNumber', label: '#' },
      { accessor: 'playerName', label: 'Player' },
      { accessor: 'playerPosition', label: 'Position' },
      { accessor: 'sessionCount', label: 'Sessions', unitType: 'count' }
    ],
    summaryColumns: [
      { accessor: 'passesAttempted', label: 'Passes', unitType: 'count' },
      { accessor: 'passCompletionPct', label: 'Pass %' },
      { accessor: 'involvementsPerMinute', label: 'No. per time' },
      {
        accessor: 'totalDistance_m',
        label: 'Distance',
        unitType: 'distance'
      },
      {
        accessor: 'highIntensityAccelCount',
        label: 'Intensity',
        unitType: 'count'
      }
    ],
    tabGroups: [
      {
        key: 'technical',
        label: 'Technical',
        tabs: [
          {
            key: 'pass',
            label: 'Pass',
            columns: [
              { accessor: 'passesAttemptedP90', label: 'P90' },
              {
                accessor: 'forwardPasses',
                label: 'Fwd Passes',
                unitType: 'count'
              },
              { accessor: 'forwardPassCompletionPct', label: 'Fwd Pass %' },
              {
                accessor: 'sidePasses',
                label: 'Side Passes',
                unitType: 'count'
              },
              { accessor: 'sidePassCompletionPct', label: 'Side Pass %' },
              {
                accessor: 'passSpeedAvg_mps',
                label: 'Avg Velocity',
                unitType: 'speed'
              },
              {
                accessor: 'passTimeOnBallAvg_s',
                label: 'Avg Time on Ball',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'shot',
            label: 'Shot',
            columns: [
              { accessor: 'shotsAttempted', label: 'Count', unitType: 'count' },
              { accessor: 'shotsAttemptedP90', label: 'P90' },
              { accessor: 'conversionPct', label: 'Conversion %' },
              { accessor: 'shotsOnTargetPct', label: 'On Target %' },
              {
                accessor: 'shotDistanceFromGoalAvg_m',
                label: 'Dist from Goal',
                unitType: 'distance'
              },
              {
                accessor: 'shotSpeedAvg_mps',
                label: 'Avg Velocity',
                unitType: 'speed'
              },
              {
                accessor: 'shotTimeOnBallAvg_s',
                label: 'Avg Time on Ball',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'carry',
            label: 'Carry',
            columns: [
              { accessor: 'carries', label: 'Count', unitType: 'count' },
              { accessor: 'carriesP90', label: 'P90' },
              { accessor: 'takeOnCount', label: 'Take Ons', unitType: 'count' },
              { accessor: 'takeOnSuccessPct', label: 'Take On %' },
              {
                accessor: 'carrySpeedAvg_mps',
                label: 'Avg Velocity',
                unitType: 'speed'
              },
              {
                accessor: 'carryDistAvg_m',
                label: 'Avg Dist',
                unitType: 'distance'
              }
            ]
          },
          {
            key: 'duels',
            label: 'Duels',
            columns: [
              { accessor: 'duelCount', label: 'Duels', unitType: 'count' },
              { accessor: 'duelWonPct', label: 'Won %' },
              { accessor: 'tackleCount', label: 'Tackles', unitType: 'count' },
              { accessor: 'tacklesWonPct', label: 'Tackles Won %' },
              {
                accessor: 'clearances',
                label: 'Clearances',
                unitType: 'count'
              },
              { accessor: 'blocks', label: 'Blocks', unitType: 'count' },
              {
                accessor: 'interceptions',
                label: 'Interceptions',
                unitType: 'count'
              }
            ]
          },
          {
            key: 'defensive_actions',
            label: 'Defensive',
            columns: [
              {
                accessor: 'defensiveActionCount',
                label: 'Count',
                unitType: 'count'
              },
              { accessor: 'defensiveActionCountP90', label: 'P90' },
              { accessor: 'tackleCount', label: 'Tackles', unitType: 'count' },
              { accessor: 'tacklesWonPct', label: 'Tackles Won %' },
              {
                accessor: 'clearances',
                label: 'Clearances',
                unitType: 'count'
              },
              { accessor: 'blocks', label: 'Blocks', unitType: 'count' },
              {
                accessor: 'interceptions',
                label: 'Interceptions',
                unitType: 'count'
              }
            ]
          },
          {
            key: 'gk_actions',
            label: 'GK',
            columns: [
              { accessor: 'gkCount', label: 'Count', unitType: 'count' },
              { accessor: 'gkCountP90', label: 'P90' },
              { accessor: 'gkSuccessPct', label: 'Success %' },
              { accessor: 'gkDiveCount', label: 'Dives', unitType: 'count' },
              {
                accessor: 'gkDiveReturnCount',
                label: 'Dive Returns',
                unitType: 'count'
              },
              {
                accessor: 'gkExplosiveImpulse_Ns',
                label: 'Explosive Impulse',
                unitType: 'impulse'
              },
              {
                accessor: 'gkImpactImpulse_Ns',
                label: 'Impact Impulse',
                unitType: 'impulse'
              }
            ]
          }
        ]
      },
      {
        key: 'physical',
        label: 'Physical',
        tabs: [
          {
            key: 'physical',
            label: 'Physical',
            columns: [
              {
                accessor: 'totalDistance_m',
                label: 'Distance',
                unitType: 'distance'
              },
              {
                accessor: 'distancePerMinute_m',
                label: 'Dist/min',
                unitType: 'distance'
              },
              {
                accessor: 'highSpeedRunDistance_m',
                label: 'HSR Distance',
                unitType: 'distance'
              },
              {
                accessor: 'sprintDistance_m',
                label: 'Sprint Distance',
                unitType: 'distance'
              },
              {
                accessor: 'highIntensityAccelCount',
                label: 'Accels',
                unitType: 'count'
              },
              {
                accessor: 'highIntensityDecelCount',
                label: 'Decels',
                unitType: 'count'
              },
              {
                accessor: 'totalMechanicalWork_kJ',
                label: 'Total Work',
                unitType: 'energy'
              }
            ]
          },
          {
            key: 'technical_load',
            label: 'Technical Load',
            columns: [
              {
                accessor: 'involvements',
                label: 'Involvements',
                unitType: 'count'
              },
              { accessor: 'involvementsPerMinute', label: 'Inv/min' },
              { accessor: 'kicks', label: 'Kicks', unitType: 'count' },
              { accessor: 'kicksPerMinute', label: 'Kicks/min' }
            ]
          },
          {
            key: 'in_possession',
            label: 'In Possession',
            columns: [
              {
                accessor: 'involvements',
                label: 'Involvements',
                unitType: 'count'
              },
              { accessor: 'involvementsPerMinute', label: 'Inv/min' },
              {
                accessor: 'involvementWorkload_J',
                label: 'Workload',
                unitType: 'energy'
              },
              {
                accessor: 'involvementWorkRate_Js',
                label: 'Work Rate',
                unitType: 'workRate'
              },
              {
                accessor: 'offTheBallRuns',
                label: 'OTB Runs',
                unitType: 'count'
              },
              { accessor: 'offTheBallRunsPerMin', label: 'OTB Runs/min' },
              {
                accessor: 'offTheBallWorkload_J',
                label: 'OTB Workload',
                unitType: 'energy'
              },
              {
                accessor: 'offTheBallWorkRate_Js',
                label: 'OTB Work Rate',
                unitType: 'workRate'
              }
            ]
          },
          {
            key: 'in_contest',
            label: 'In Contest',
            columns: [
              { accessor: 'duelCount', label: 'Duels', unitType: 'count' },
              { accessor: 'duelRuns', label: 'Duel Runs', unitType: 'count' },
              {
                accessor: 'duelWorkload_J',
                label: 'Workload',
                unitType: 'energy'
              },
              {
                accessor: 'duelWorkRate_Js',
                label: 'Work Rate',
                unitType: 'workRate'
              },
              {
                accessor: 'offTheBallRuns',
                label: 'OTB Runs',
                unitType: 'count'
              },
              { accessor: 'offTheBallRunsPerMin', label: 'OTB Runs/min' },
              {
                accessor: 'offTheBallWorkload_J',
                label: 'OTB Workload',
                unitType: 'energy'
              },
              {
                accessor: 'offTheBallWorkRate_Js',
                label: 'OTB Work Rate',
                unitType: 'workRate'
              }
            ]
          },
          {
            key: 'out_of_possession',
            label: 'Out of Possession',
            columns: [
              {
                accessor: 'defensiveActionCount',
                label: 'Def Actions',
                unitType: 'count'
              },
              { accessor: 'defensiveActionCountP90', label: 'Def P90' },
              {
                accessor: 'defensiveActionRuns',
                label: 'Def Runs',
                unitType: 'count'
              },
              {
                accessor: 'defensiveActionWorkload_J',
                label: 'Workload',
                unitType: 'energy'
              },
              {
                accessor: 'defensiveActionWorkRate_Js',
                label: 'Work Rate',
                unitType: 'workRate'
              },
              {
                accessor: 'pressureCount',
                label: 'Pressures',
                unitType: 'count'
              },
              { accessor: 'pressureRunsPerMin', label: 'Press Runs/min' },
              {
                accessor: 'pressureRunWorkload_J',
                label: 'Press Workload',
                unitType: 'energy'
              },
              {
                accessor: 'pressureRunWorkRate_Js',
                label: 'Press Work Rate',
                unitType: 'workRate'
              },
              {
                accessor: 'offTheBallRuns',
                label: 'OTB Runs',
                unitType: 'count'
              },
              { accessor: 'offTheBallRunsPerMin', label: 'OTB Runs/min' },
              {
                accessor: 'offTheBallWorkload_J',
                label: 'OTB Workload',
                unitType: 'energy'
              },
              {
                accessor: 'offTheBallWorkRate_Js',
                label: 'OTB Work Rate',
                unitType: 'workRate'
              }
            ]
          }
        ]
      }
    ]
  },

  teamTable: {
    staticColumns: [
      { accessor: 'playerNumber', label: '#' },
      { accessor: 'playerName', label: 'Player' },
      { accessor: 'playerPosition', label: 'Pos' }
    ],
    tabGroups: [
      {
        key: 'technical',
        label: 'Technical',
        tabs: [
          {
            key: 'pass',
            label: 'Pass',
            columns: [
              {
                accessor: 'passesAttempted',
                label: 'Count',
                unitType: 'count'
              },
              {
                accessor: 'forwardPasses',
                label: 'Fwd Pass',
                unitType: 'count'
              },
              { accessor: 'forwardPassCompletionPct', label: 'Fwd %' },
              { accessor: 'sidePasses', label: 'Side', unitType: 'count' },
              { accessor: 'sidePassCompletionPct', label: 'Side %' },
              { accessor: 'backPasses', label: 'Back', unitType: 'count' },
              { accessor: 'backPassCompletionPct', label: 'Back %' },
              {
                accessor: 'passSpeedAvg_mps',
                label: 'Avg Velocity',
                unitType: 'speed'
              },
              {
                accessor: 'passTimeOnBallAvg_s',
                label: 'Time on Ball',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'shot',
            label: 'Shot',
            columns: [
              { accessor: 'shotsAttempted', label: 'Count', unitType: 'count' },
              { accessor: 'shotsAttemptedP90', label: 'P90' },
              {
                accessor: 'shotSpeedAvg_mps',
                label: 'Avg Velocity',
                unitType: 'speed'
              },
              {
                accessor: 'shotTimeOnBallAvg_s',
                label: 'Time on Ball',
                unitType: 'time'
              },
              {
                accessor: 'shotDistanceFromGoalAvg_m',
                label: 'Dist from Goal',
                unitType: 'distance'
              }
            ]
          },
          {
            key: 'carry',
            label: 'Carry',
            columns: [
              { accessor: 'carries', label: 'Count', unitType: 'count' },
              { accessor: 'carriesP90', label: 'P90' },
              {
                accessor: 'carrySpeedAvg_mps',
                label: 'Avg Velocity',
                unitType: 'speed'
              },
              {
                accessor: 'carryDistAvg_m',
                label: 'Avg Dist',
                unitType: 'distance'
              }
            ]
          },
          { key: 'compare', label: 'Compare', columns: [] }
        ]
      },
      {
        key: 'physical',
        label: 'Physical',
        tabs: [
          {
            key: 'physical',
            label: 'Physical',
            columns: [
              {
                accessor: 'totalDistance_m',
                label: 'Distance',
                unitType: 'distance'
              },
              {
                accessor: 'distancePerMinute_m',
                label: 'Dist/min',
                unitType: 'distance'
              },
              {
                accessor: 'highSpeedRunDistance_m',
                label: 'HSR Distance',
                unitType: 'distance'
              },
              {
                accessor: 'sprintDistance_m',
                label: 'Sprint Dist',
                unitType: 'distance'
              },
              { accessor: 'sprintCount', label: 'Sprints', unitType: 'count' },
              {
                accessor: 'highIntensityAccelCount',
                label: 'Accels',
                unitType: 'count'
              },
              {
                accessor: 'highIntensityDecelCount',
                label: 'Decels',
                unitType: 'count'
              },
              {
                accessor: 'totalMechanicalWork_kJ',
                label: 'Total Work',
                unitType: 'energy'
              },
              {
                accessor: 'maxSpeed_mps',
                label: 'Max Speed',
                unitType: 'speed'
              }
            ]
          },
          {
            key: 'technical_load',
            label: 'Technical Load',
            columns: [
              {
                accessor: 'involvements',
                label: 'Involvements',
                unitType: 'count'
              },
              { accessor: 'involvementsP90', label: 'Inv P90' },
              { accessor: 'involvementsPerMinute', label: 'Inv/min' },
              { accessor: 'kicks', label: 'Kicks', unitType: 'count' },
              { accessor: 'kicksPerMinute', label: 'Kicks/min' }
            ]
          }
        ]
      }
    ],
    chartCategories: ['Pass', 'Shot', 'Carry']
  },

  monitoring: {
    physicalMetrics: [
      { key: 'totalDistance_m', label: 'Distance', unitType: 'distance' },
      {
        key: 'distancePerMinute_m',
        label: 'Distance/min',
        unitType: 'distance'
      },
      {
        key: 'highSpeedRunDistance_m',
        label: 'HSR Distance',
        unitType: 'distance'
      },
      { key: 'highIntensityAccelCount', label: 'Accels', unitType: 'count' },
      { key: 'highIntensityDecelCount', label: 'Decels', unitType: 'count' },
      {
        key: 'totalMechanicalWork_kJ',
        label: 'Total Work',
        unitType: 'energy'
      }
    ],
    technicalMetrics: [
      { key: 'involvements', label: 'Involvements', unitType: 'count' },
      { key: 'involvementsPerMinute', label: 'Inv/min' },
      { key: 'forwardPasses', label: 'Forward Passes', unitType: 'count' },
      { key: 'forwardPassCompletionPct', label: 'Fwd Pass %' },
      { key: 'shotsAttempted', label: 'Shots', unitType: 'count' },
      { key: 'conversionPct', label: 'Shot Conversion' }
    ]
  }
}

export class SoccerConfig extends ReportingSportConfig<SoccerPosition> {
  constructor(unitSystem: UnitSystem) {
    super(unitSystem, soccerData)
  }
}
